// One feature's properties as the sidebar entry shows them: tags grouped by key prefix
// (layer id, `orbis`, `osm` or none) with the revision once per group, the viewer's own
// properties apart, and relation members. Pure, so the node tests reach it.
import { baseKey, lrKindOfKey, parseLrChain } from "./lrValues.mjs";
import { parseMembers } from "./members.mjs";

// Set by viewer code (layers, addfeature handlers), not by the WFS.
const VIEWER_KEYS = [
  "lengthCm",
  "laneRoadClass",
  "overlap-offset",
  "overlap-start-node",
  "overlap-end-node",
  "is-relation",
  "relation-id",
  "curvature_at_junction",
  "crosslinkUncoveredGeoms",
  "crosslinkOutOfBoundsGeoms",
  "source-feature-id",
  "nonInteractive",
];
const VIEWER = new Set(VIEWER_KEYS);
// Group header of keys without a layer or source prefix (sidebar entry and hover box).
export const NO_PREFIX_LABEL = "no prefix";
// Internal flags of the viewer's own drawing: not shown (the raw box still has them).
const VIEWER_HIDDEN = new Set([
  "is-relation",
  "relation-id",
  "overlap-offset",
  "overlap-start-node",
  "overlap-end-node",
  "crosslinkUncoveredGeoms",
  "crosslinkOutOfBoundsGeoms",
  "source-feature-id",
  "nonInteractive",
]);

// Linear chains whose key carries no `:linear` suffix.
const LINEAR_WITHOUT_SUFFIX = new Set(["meta_full_curvature"]);

// A prefix is a layer id or a source name; it never holds a `:`.
const PREFIXED = /^([^-:]+)-(.+)$/;
const NUMERIC = /^\d+$/;

const isSkipped = (key) =>
  key === "geometry" ||
  key === "type" ||
  key === "meta-uuid" ||
  key.startsWith("revision-") ||
  key.startsWith("relation-member-");

function splitPrefix(rawKey) {
  const match = PREFIXED.exec(rawKey);
  return match
    ? { prefix: match[1], key: match[2] }
    : { prefix: "", key: rawKey };
}

/** `revision-<layer>-<key>` keys by their key part. */
function indexRevisions(properties) {
  const byKeyPart = new Map();
  Object.keys(properties).forEach((key) => {
    if (!key.startsWith("revision-")) return;
    const match = PREFIXED.exec(key.slice("revision-".length));
    if (!match) return;
    const [, layer, keyPart] = match;
    if (!byKeyPart.has(keyPart)) byKeyPart.set(keyPart, []);
    byKeyPart.get(keyPart).push({ layer, revKey: key });
  });
  return byKeyPart;
}

function mostFrequent(values) {
  const counts = new Map();
  values.forEach((v) => counts.set(v, (counts.get(v) ?? 0) + 1));
  let best;
  counts.forEach((count, value) => {
    if (best === undefined || count > counts.get(best)) best = value;
  });
  return best;
}

/**
 * @param {{id: string, properties: Record<string, unknown>, geometryType: string,
 *   lengthCm: number|null|undefined}} input
 */
export function buildFeatureModel({ id, properties, geometryType, lengthCm }) {
  const props = properties ?? {};
  const revisionsByKeyPart = indexRevisions(props);
  const groups = [];
  const groupByPrefix = new Map();
  const viewer = [];
  const revisionLayerOf = new Map();

  Object.entries(props).forEach(([rawKey, value]) => {
    if (VIEWER.has(rawKey)) {
      if (VIEWER_HIDDEN.has(rawKey)) return;
      viewer.push({
        key: rawKey,
        rawKey,
        value,
        revision: undefined,
        lr: null,
        sections: null,
      });
      return;
    }
    if (isSkipped(rawKey)) return;
    const { prefix, key } = splitPrefix(rawKey);
    // WFSLayer renames a sectioned tag to `<key>:step`; its revision may keep the old key.
    let revision =
      props[`revision-${rawKey}`] ??
      props[`revision-${rawKey.replace(/:step$/, "")}`];
    let revisionLayer = NUMERIC.test(prefix) ? prefix : null;
    if (revision === undefined && !NUMERIC.test(prefix)) {
      // orbis-/osm-/unprefixed keys: the revision sits under the layer that wrote it.
      const candidates = revisionsByKeyPart.get(key) ?? [];
      if (candidates.length === 1) {
        revision = props[candidates[0].revKey];
        revisionLayer = candidates[0].layer;
      }
    }
    const lr =
      lrKindOfKey(rawKey) ??
      (LINEAR_WITHOUT_SUFFIX.has(baseKey(key)) ? "linear" : null);
    const tag = {
      key,
      rawKey,
      value,
      revision: revision === undefined ? undefined : String(revision),
      lr,
      sections: lr ? parseLrChain(value) : null,
    };
    if (!groupByPrefix.has(prefix)) {
      const group = {
        prefix,
        revisionLayer: null,
        revision: undefined,
        tags: [],
      };
      groupByPrefix.set(prefix, group);
      groups.push(group);
    }
    const group = groupByPrefix.get(prefix);
    group.tags.push(tag);
    revisionLayerOf.set(tag, revisionLayer);
  });

  groups.forEach((group) => {
    group.revision = mostFrequent(
      group.tags.map((t) => t.revision).filter((r) => r !== undefined),
    );
    if (!NUMERIC.test(group.prefix) && group.revision !== undefined) {
      const tag = group.tags.find((t) => t.revision === group.revision);
      group.revisionLayer = revisionLayerOf.get(tag);
    }
  });

  const tagKeys = new Set(groups.flatMap((g) => g.tags.map((t) => t.key)));
  const orphanRevisions = [];
  revisionsByKeyPart.forEach((entries, keyPart) => {
    if (!tagKeys.has(keyPart))
      entries.forEach((e) => orphanRevisions.push(e.revKey));
  });
  const order = Object.keys(props);
  orphanRevisions.sort((a, b) => order.indexOf(a) - order.indexOf(b));

  return {
    id,
    type: props.type === undefined ? undefined : String(props.type),
    geometryType,
    lengthCm: Number.isFinite(lengthCm) ? lengthCm : null,
    groups,
    viewer,
    members: parseMembers(props),
    orphanRevisions,
    properties: props,
  };
}
